/**
 * Cataloghi e tipi di dominio condivisi fra API e web.
 *
 * Sono dati e forme, non regole: le regole vivono in planner/ e budget/.
 */

import type { TripRole } from './rbac.js';

/* -------------------------------------------------------------------------- */
/* Stato del dato — regola trasversale: mai inventare cio' che non sappiamo    */
/* -------------------------------------------------------------------------- */

export const DATA_STATUSES = ['verified', 'estimated', 'unavailable', 'stale'] as const;
export type DataStatus = (typeof DATA_STATUSES)[number];

export interface Sourced<T> {
  readonly value: T | null;
  readonly status: DataStatus;
  readonly source?: string;
  readonly checkedAt?: string;
}

export function unavailable(source?: string): Sourced<never> {
  return source === undefined
    ? { value: null, status: 'unavailable' }
    : { value: null, status: 'unavailable', source };
}

/* -------------------------------------------------------------------------- */
/* Viaggio                                                                     */
/* -------------------------------------------------------------------------- */

export const TRIP_STATUSES = ['draft', 'planned', 'active', 'completed', 'archived'] as const;
export type TripStatus = (typeof TRIP_STATUSES)[number];

export const TRAVEL_PARTIES = ['solo', 'couple', 'friends', 'family', 'group'] as const;
export type TravelParty = (typeof TRAVEL_PARTIES)[number];

export const TRAVEL_PACES = ['relaxed', 'balanced', 'intense'] as const;
export type TravelPace = (typeof TRAVEL_PACES)[number];

/**
 * Il ritmo modifica REALMENTE il planner: numero di tappe, margine fra una tappa
 * e l'altra, durata delle pause. Non e' un'etichetta decorativa.
 */
export interface PaceProfile {
  readonly minStops: number;
  readonly maxStops: number;
  /** Margine aggiuntivo (minuti) fra la fine di una tappa e l'inizio della successiva. */
  readonly slackMin: number;
  /** Moltiplicatore applicato alla durata suggerita della visita. */
  readonly visitFactor: number;
  /** Deviazione massima accettata per raggiungere una tappa, in minuti. */
  readonly maxDetourMin: number;
  readonly label: string;
}

export const PACE_PROFILES: Record<TravelPace, PaceProfile> = {
  relaxed: { minStops: 2, maxStops: 3, slackMin: 30, visitFactor: 1.2, maxDetourMin: 20, label: 'Relax' },
  balanced: { minStops: 3, maxStops: 5, slackMin: 15, visitFactor: 1.0, maxDetourMin: 30, label: 'Equilibrato' },
  intense: { minStops: 5, maxStops: 7, slackMin: 5, visitFactor: 0.85, maxDetourMin: 45, label: 'Intenso' },
};

export const TRANSPORT_MODES = ['walk', 'bike', 'car', 'transit', 'taxi'] as const;
export type TransportMode = (typeof TRANSPORT_MODES)[number];

export const MEAL_SLOTS = ['breakfast', 'lunch', 'dinner'] as const;
export type MealSlot = (typeof MEAL_SLOTS)[number];

export const MEAL_LABELS: Record<MealSlot, string> = {
  breakfast: 'Colazione',
  lunch: 'Pranzo',
  dinner: 'Cena',
};

/** Finestre pasto di default, in orario locale della destinazione. */
export const DEFAULT_MEAL_WINDOWS: Record<
  MealSlot,
  { from: string; to: string; durationMin: number }
> = {
  breakfast: { from: '07:30', to: '09:30', durationMin: 30 },
  lunch: { from: '12:00', to: '14:30', durationMin: 60 },
  dinner: { from: '19:00', to: '21:30', durationMin: 90 },
};

export const INTERESTS = [
  'monuments', 'museums', 'history', 'nature', 'parks', 'hiking', 'viewpoints',
  'sea', 'food', 'nightlife_bars', 'shopping', 'art', 'photography', 'kids',
  'unique_experiences', 'nightlife',
] as const;
export type Interest = (typeof INTERESTS)[number];

export const INTEREST_LABELS: Record<Interest, string> = {
  monuments: 'Monumenti',
  museums: 'Musei',
  history: 'Storia',
  nature: 'Natura',
  parks: 'Parchi',
  hiking: 'Trekking',
  viewpoints: 'Panorami',
  sea: 'Mare',
  food: 'Gastronomia',
  nightlife_bars: 'Locali',
  shopping: 'Shopping',
  art: 'Arte',
  photography: 'Fotografia',
  kids: 'Con i bambini',
  unique_experiences: 'Esperienze particolari',
  nightlife: 'Vita notturna',
};

export const DIETS = ['none', 'vegetarian', 'vegan', 'gluten_free', 'halal', 'kosher', 'other'] as const;
export type Diet = (typeof DIETS)[number];

export const DIET_LABELS: Record<Diet, string> = {
  none: 'Nessuna preferenza',
  vegetarian: 'Vegetariano',
  vegan: 'Vegano',
  gluten_free: 'Senza glutine',
  halal: 'Halal',
  kosher: 'Kosher',
  other: 'Altro',
};

/* -------------------------------------------------------------------------- */
/* Luoghi                                                                      */
/* -------------------------------------------------------------------------- */

/** Categoria del POI: coincide con la chiave di `visit_duration_rules`. */
export const PLACE_CATEGORIES = [
  'viewpoint', 'monument', 'monument_large', 'church', 'museum_small', 'museum_large',
  'gallery', 'castle', 'archaeological', 'park', 'garden', 'nature_reserve', 'beach',
  'waterfall', 'cave', 'peak', 'hiking', 'zoo', 'theme_park', 'historic_district',
  'market', 'square', 'attraction', 'restaurant', 'cafe', 'bar', 'shopping', 'experience',
] as const;
export type PlaceCategory = (typeof PLACE_CATEGORIES)[number];

export const PLACE_CATEGORY_LABELS: Record<PlaceCategory, string> = {
  viewpoint: 'Punto panoramico',
  monument: 'Monumento',
  monument_large: 'Sito monumentale',
  church: 'Chiesa',
  museum_small: 'Museo',
  museum_large: 'Grande museo',
  gallery: "Galleria d'arte",
  castle: 'Castello',
  archaeological: 'Sito archeologico',
  park: 'Parco',
  garden: 'Giardino',
  nature_reserve: 'Riserva naturale',
  beach: 'Spiaggia',
  waterfall: 'Cascata',
  cave: 'Grotta',
  peak: 'Vetta',
  hiking: 'Percorso',
  zoo: 'Zoo / acquario',
  theme_park: 'Parco tematico',
  historic_district: 'Quartiere storico',
  market: 'Mercato',
  square: 'Piazza',
  attraction: 'Attrazione',
  restaurant: 'Ristorante',
  cafe: 'Caffè',
  bar: 'Bar / locale',
  shopping: 'Shopping',
  experience: 'Esperienza',
};

/** Sezioni della schermata Scopri. Ognuna e' un insieme di categorie. */
export const DISCOVER_SECTIONS = [
  'highlights', 'monuments', 'museums', 'art', 'nature', 'parks', 'reserves',
  'beaches', 'hiking', 'viewpoints', 'districts', 'markets', 'experiences',
  'family', 'free', 'restaurants', 'bars',
] as const;
export type DiscoverSection = (typeof DISCOVER_SECTIONS)[number];

export const DISCOVER_SECTION_LABELS: Record<DiscoverSection, string> = {
  highlights: 'Da non perdere',
  monuments: 'Monumenti',
  museums: 'Musei',
  art: 'Arte',
  nature: 'Natura',
  parks: 'Parchi',
  reserves: 'Riserve',
  beaches: 'Spiagge',
  hiking: 'Trekking',
  viewpoints: 'Punti panoramici',
  districts: 'Quartieri',
  markets: 'Mercati',
  experiences: 'Attività',
  family: 'Famiglie',
  free: 'Gratis',
  restaurants: 'Ristoranti',
  bars: 'Bar e locali',
};

export const DISCOVER_SECTION_CATEGORIES: Record<DiscoverSection, PlaceCategory[]> = {
  highlights: ['monument_large', 'museum_large', 'archaeological', 'castle', 'viewpoint', 'historic_district'],
  monuments: ['monument', 'monument_large', 'church', 'castle', 'archaeological', 'square'],
  museums: ['museum_small', 'museum_large'],
  art: ['gallery', 'museum_small', 'museum_large'],
  nature: ['nature_reserve', 'waterfall', 'cave', 'peak', 'beach', 'garden'],
  parks: ['park', 'garden'],
  reserves: ['nature_reserve'],
  beaches: ['beach'],
  hiking: ['hiking', 'peak'],
  viewpoints: ['viewpoint'],
  districts: ['historic_district', 'square'],
  markets: ['market'],
  experiences: ['experience', 'attraction', 'theme_park'],
  family: ['zoo', 'theme_park', 'park', 'experience'],
  free: ['park', 'square', 'viewpoint', 'historic_district', 'church', 'garden', 'market'],
  restaurants: ['restaurant'],
  bars: ['bar', 'cafe'],
};

export const PRICE_TIERS = ['€', '€€', '€€€'] as const;
export type PriceTier = (typeof PRICE_TIERS)[number];

/** Stima del costo di un pasto per fascia, in minor unit (EUR). Configurabile. */
export const MEAL_COST_ESTIMATE_MINOR: Record<PriceTier, { min: number; max: number }> = {
  '€': { min: 800, max: 1800 },
  '€€': { min: 1800, max: 3500 },
  '€€€': { min: 3500, max: 8000 },
};

export interface OpeningInterval {
  /** 0 = domenica ... 6 = sabato */
  readonly day: number;
  readonly from: string;
  readonly to: string;
}

export interface OpeningHoursInfo {
  readonly status: DataStatus;
  readonly raw: string | null;
  readonly intervals: OpeningInterval[];
  readonly lastEntryOffsetMin: number | null;
}

export interface PlaceSummary {
  readonly id: string;
  readonly name: string;
  readonly category: PlaceCategory;
  readonly categoryLabel: string;
  readonly lat: number;
  readonly lon: number;
  readonly address: string | null;
  readonly description: string | null;
  readonly descriptionSource: string | null;
  readonly imageUrl: string | null;
  readonly imageAttribution: string | null;
  /** Pagina di origine su Wikimedia Commons: parte dell’attribuzione. */
  readonly imagePageUrl: string | null;
  /**
   * Voto da 0 a 5. Null quando i voti sono spenti o quando la fonte non conosce
   * il posto: non e' mai uno zero, che significherebbe "pessimo".
   */
  readonly rating: number | null;
  readonly ratingsCount: number | null;
  /** Chi ha dato quel voto. L'attribuzione e' obbligatoria, non decorativa. */
  readonly ratingSource: string | null;
  readonly ratingUrl: string | null;
  readonly website: string | null;
  readonly phone: string | null;
  readonly wheelchair: string | null;
  readonly accessibilityStatus: DataStatus;
  readonly admissionAmountMinor: number | null;
  readonly admissionCurrency: string | null;
  readonly admissionStatus: DataStatus;
  readonly estimatedVisitMin: number | null;
  readonly visitDurationStatus: DataStatus;
  readonly cuisine: string[];
  readonly diets: string[];
  readonly priceTier: PriceTier | null;
  readonly takeaway: boolean | null;
  readonly outdoorSeating: boolean | null;
  readonly openingHours: OpeningHoursInfo;
  readonly sources: Array<{ source: string; url: string | null; license: string | null }>;
  /** Distanza in metri dal punto di riferimento della ricerca, se richiesta. */
  readonly distanceM: number | null;
  /** Regola OSM sull'ingresso gratuito (fee:conditional). null = non lo sappiamo. */
  readonly feeConditional: string | null;
  /** Quando abbiamo riletto il luogo dalla fonte. null = mai dopo l'import. */
  readonly refreshedAt: string | null;
}

export interface WishlistEntry {
  readonly id: string;
  readonly placeId: string;
  readonly note: string | null;
  readonly priority: 1 | 2 | 3;
  readonly addedByName: string | null;
  readonly createdAt: string;
  readonly place: PlaceSummary;
  /** Giornata in cui è già stato collocato, se lo è. */
  readonly scheduledDayId: string | null;
}

/* -------------------------------------------------------------------------- */
/* Budget                                                                      */
/* -------------------------------------------------------------------------- */

export const BUDGET_CATEGORIES = [
  'accommodation', 'attractions', 'food', 'transport', 'shopping', 'extra',
] as const;
export type BudgetCategory = (typeof BUDGET_CATEGORIES)[number];

export const BUDGET_CATEGORY_LABELS: Record<BudgetCategory, string> = {
  accommodation: 'Alloggio',
  attractions: 'Attrazioni',
  food: 'Cibo',
  transport: 'Trasporti',
  shopping: 'Shopping',
  extra: 'Extra',
};

export const BUDGET_MODES = ['none', 'total', 'per_person', 'daily'] as const;
export type BudgetMode = (typeof BUDGET_MODES)[number];

/** Semaforo a quattro livelli. Il rosso e' riservato ai problemi reali. */
export const BUDGET_LEVELS = ['ok', 'warn', 'over', 'blocked'] as const;
export type BudgetLevel = (typeof BUDGET_LEVELS)[number];

export interface BudgetSettings {
  readonly id: string | null;
  readonly scope: 'trip' | 'personal';
  readonly totalMinor: number | null;
  readonly dailyMinor: number | null;
  readonly perPerson: boolean;
  readonly currency: string;
  readonly categories: Array<{ category: BudgetCategory; plannedMinor: number }>;
}

export interface DayBudgetState {
  readonly date: string;
  readonly plannedMinor: number;
  readonly actualMinor: number;
  readonly limitMinor: number | null;
  readonly level: BudgetLevel;
}

export interface BudgetOverview {
  readonly currency: string;
  readonly totalLimitMinor: number | null;
  readonly totalPlannedMinor: number;
  readonly totalActualMinor: number;
  readonly level: BudgetLevel;
  /**
   * Tappe di cui NON conosciamo il prezzo. Senza questo numero un "previsto
   * €0" sembrerebbe "tutto gratis", mentre spesso significa solo che il dato
   * non c'e'.
   */
  readonly itemsWithUnknownCost: number;
  readonly days: DayBudgetState[];
  readonly byCategory: Array<{
    category: BudgetCategory;
    plannedMinor: number;
    actualMinor: number;
    limitMinor: number | null;
  }>;
}

/* -------------------------------------------------------------------------- */
/* Itinerario                                                                  */
/* -------------------------------------------------------------------------- */

export const ITEM_KINDS = ['poi', 'meal', 'transport', 'accommodation', 'free', 'reservation'] as const;
export type ItemKind = (typeof ITEM_KINDS)[number];

export interface ItineraryItemSummary {
  readonly id: string;
  readonly tripDayId: string;
  readonly kind: ItemKind;
  readonly placeId: string | null;
  readonly title: string;
  readonly notes: string | null;
  readonly plannedStart: string | null;
  readonly plannedEnd: string | null;
  readonly durationMin: number;
  readonly durationStatus: DataStatus;
  readonly isLocked: boolean;
  readonly sortIndex: number;
  readonly estimatedCostMinor: number | null;
  readonly estimatedCostCurrency: string | null;
  readonly costStatus: DataStatus;
  readonly mealSlot: MealSlot | null;
  readonly lat: number | null;
  readonly lon: number | null;
  readonly category: PlaceCategory | null;
  /**
   * Foto del luogo, ospitata da Wikimedia: l'app conserva solo l'indirizzo.
   * Null quando non ne esiste una attribuibile.
   */
  readonly imageUrl: string | null;
  readonly imageAttribution: string | null;
  readonly openingHours: OpeningHoursInfo | null;
  /** Avvisi calcolati: chiusura, orario impossibile, budget. Mai inventati. */
  readonly warnings: ItemWarning[];
}

export interface ItemWarning {
  readonly code:
    | 'closed_that_day'
    | 'closes_during_visit'
    | 'before_opening'
    | 'after_last_entry'
    | 'hours_unknown'
    | 'over_day_budget'
    | 'outside_day_window';
  readonly message: string;
  readonly severity: 'info' | 'warn' | 'error';
}

export interface TransportLegSummary {
  readonly id: string;
  readonly fromItemId: string | null;
  readonly toItemId: string | null;
  readonly mode: TransportMode;
  readonly durationSec: number;
  readonly distanceM: number;
  readonly recommendedDeparture: string | null;
  readonly expectedArrival: string | null;
  readonly status: DataStatus;
  readonly source: string;
}

export interface TripDaySummary {
  readonly id: string;
  readonly date: string;
  readonly dayIndex: number;
  readonly dayStartLocal: string;
  readonly dayEndLocal: string;
  readonly dailyBudgetMinor: number | null;
  readonly notes: string | null;
}

/**
 * L'alloggio da cui la giornata parte e a cui torna.
 *
 * `lat`/`lon` possono mancare: significa che l'indirizzo non e' stato
 * riconosciuto: l'alloggio si mostra lo stesso, ma non puo' entrare nel calcolo
 * degli spostamenti. Chi legge deve poterlo capire, non indovinarlo.
 */
export interface DayBase {
  readonly id: string;
  readonly name: string;
  readonly address: string | null;
  readonly lat: number | null;
  readonly lon: number | null;
}

export interface TripDayDetail extends TripDaySummary {
  /** Alloggio della giornata. `null` se il viaggio non ne ha ancora uno. */
  readonly base: DayBase | null;
  readonly items: ItineraryItemSummary[];
  readonly legs: TransportLegSummary[];
  readonly plannedCostMinor: number;
  readonly actualCostMinor: number;
  readonly budgetLevel: BudgetLevel;
}

/* -------------------------------------------------------------------------- */
/* Entita' esposte dall'API                                                    */
/* -------------------------------------------------------------------------- */

export interface PublicUser {
  readonly id: string;
  readonly email: string;
  readonly displayName: string;
  readonly locale: string;
  readonly baseCurrency: string;
  readonly createdAt: string;
}

export interface MemberSummary {
  readonly userId: string;
  readonly displayName: string;
  readonly role: TripRole;
  readonly joinedAt: string;
}

export interface DestinationSummary {
  readonly id: string;
  readonly name: string;
  readonly countryCode: string | null;
  readonly timezone: string;
  readonly lat: number;
  readonly lon: number;
  readonly poiIngestedAt: string | null;
  readonly poiCount: number;
}

export interface AccommodationSummary {
  readonly id: string;
  readonly name: string;
  readonly address: string | null;
  readonly lat: number | null;
  readonly lon: number | null;
  readonly checkInAt: string | null;
  readonly checkOutAt: string | null;
  readonly costAmountMinor: number | null;
  readonly costCurrency: string | null;
  readonly bookingReference: string | null;
  readonly notes: string | null;
  readonly phone: string | null;
  readonly email: string | null;
  readonly website: string | null;
}

export interface TripSummary {
  readonly id: string;
  readonly name: string;
  readonly status: TripStatus;
  readonly destination: DestinationSummary;
  readonly startsAt: string;
  readonly endsAt: string;
  readonly party: TravelParty;
  readonly partySize: number;
  readonly pace: TravelPace;
  readonly interests: Interest[];
  readonly diets: Diet[];
  readonly transportModes: TransportMode[];
  readonly baseCurrency: string;
  readonly safetyBufferMin: number;
  readonly dayStartLocal: string;
  readonly maxReturnTime: string | null;
  readonly mealsEnabled: Record<MealSlot, boolean>;
  readonly role: TripRole;
  readonly memberCount: number;
  readonly dayCount: number;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface TripDetail extends TripSummary {
  readonly members: MemberSummary[];
  readonly accommodations: AccommodationSummary[];
  readonly days: TripDaySummary[];
}

export interface InviteSummary {
  readonly id: string;
  readonly role: TripRole;
  readonly expiresAt: string;
  readonly maxUses: number;
  readonly uses: number;
  readonly revokedAt: string | null;
  /** Presente SOLO alla creazione: dopo il server conserva solo l'hash. */
  readonly url?: string;
}

export interface ExpenseSummary {
  readonly id: string;
  readonly tripDayId: string | null;
  readonly itineraryItemId: string | null;
  readonly category: BudgetCategory;
  readonly description: string;
  readonly amountMinor: number;
  readonly currency: string;
  readonly baseAmountMinor: number;
  readonly fxRate: string;
  readonly fxDate: string | null;
  readonly paidBy: string | null;
  readonly paidByName: string | null;
  readonly paidAt: string;
  readonly isShared: boolean;
  readonly splits: Array<{ userId: string; displayName: string; shareMinor: number }>;
}

export interface SettlementEntry {
  readonly fromUserId: string;
  readonly fromName: string;
  readonly toUserId: string;
  readonly toName: string;
  readonly amountMinor: number;
  readonly currency: string;
}

export interface FlightSummary {
  readonly id: string;
  readonly direction: 'outbound' | 'return' | 'other';
  readonly airline: string | null;
  readonly flightNumber: string | null;
  readonly depAirport: string;
  readonly arrAirport: string;
  readonly depTerminal: string | null;
  readonly arrTerminal: string | null;
  readonly gate: string | null;
  readonly depScheduled: string;
  readonly arrScheduled: string | null;
  readonly bookingReference: string | null;
  readonly seat: string | null;
  readonly notes: string | null;
  readonly airportBufferMin: number;
  readonly checkinOpensAt: string | null;
  /** Ordine della tratta: 1 = prima. Con piu' tratte c'e' uno scalo. */
  readonly legOrder: number;
  /** Capacita' realmente disponibili: senza provider, realtime = false. */
  readonly statusSource: string;
  readonly realtimeAvailable: boolean;
}

export interface ChecklistItemSummary {
  readonly id: string;
  readonly category: string;
  readonly label: string;
  readonly state: 'todo' | 'done' | 'missing' | 'na';
  readonly autoRule: string | null;
  readonly sortIndex: number;
}

export interface ProposalSummary {
  readonly id: string;
  readonly placeId: string | null;
  readonly title: string;
  readonly note: string | null;
  readonly proposedBy: string | null;
  readonly proposedByName: string | null;
  readonly status: 'open' | 'accepted' | 'rejected';
  readonly createdAt: string;
  readonly upvotes: number;
  readonly downvotes: number;
  readonly myVote: -1 | 0 | 1;
}

export interface CommentSummary {
  readonly id: string;
  readonly userId: string | null;
  readonly displayName: string | null;
  readonly body: string;
  readonly createdAt: string;
}

/* -------------------------------------------------------------------------- */
/* Home "cosa faccio adesso"                                                   */
/* -------------------------------------------------------------------------- */

export interface NextActionInfo {
  readonly item: ItineraryItemSummary | null;
  readonly leg: TransportLegSummary | null;
  /** Minuti mancanti alla partenza consigliata. Negativo = dovevi gia' partire. */
  readonly minutesUntilDeparture: number | null;
  readonly recommendedDeparture: string | null;
  readonly message: string;
}

export interface TodayOverview {
  readonly tripId: string;
  readonly mode: 'planning' | 'travelling';
  readonly localDate: string | null;
  readonly localTime: string | null;
  readonly day: TripDayDetail | null;
  readonly nextAction: NextActionInfo;
  readonly upcoming: ItineraryItemSummary[];
  readonly nextMeal: { slot: MealSlot; label: string; from: string; to: string } | null;
  readonly budget: { spentMinor: number; limitMinor: number | null; level: BudgetLevel; currency: string } | null;
  readonly missingCount: number;
  readonly alerts: Array<{ code: string; message: string; severity: 'info' | 'warn' | 'error' }>;
}
